import { ArrowLeft, Moon } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import welcomeBg from "../../assets/homebg.png";

const SleepRecoveryPage = () => {
  const navigate = useNavigate();

  // Weekly sleep data (in hours)
  const weeklyData = [
    { day: "Mon", hours: 7.5 },
    { day: "Tue", hours: 6.8 },
    { day: "Wed", hours: 8.2 },
    { day: "Thu", hours: 6.0 },
    { day: "Fri", hours: 6.5 },
    { day: "Sat", hours: 7.8 },
    { day: "Sun", hours: 6.9 },
  ];

  const goalHours = 8;
  const maxHours = 10;

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-8"
        style={{ height: "calc(100vh - 0px)" }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-xl bg-card/80 hover:bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold text-foreground">
            Sleep Recovery
          </h1>
        </div>

        {/* Last Night Card */}
        <Card className="rounded-xl bg-card p-4 shadow-sm">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-purple-100 p-3 dark:bg-purple-900/30">
                <Moon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                  Last Night
                </p>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-foreground">
                    --
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    hrs
                  </span>
                </div>
              </div>
            </div>
            <span className="rounded-md bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-400">
              No Data
            </span>
          </div>
        </Card>

        {/* Sleep Debt Section */}
        <Card className="rounded-xl bg-card p-4 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-foreground">
            Sleep Debt
          </h3>

          <div className="mb-2 flex items-baseline gap-1">
            <span className="text-2xl font-bold text-gray-600 dark:text-gray-400">
              -- hrs
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              below 8hr target
            </span>
          </div>

          {/* Progress Bar */}
          <div className="mb-1 h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className="h-full rounded-full bg-gradient-to-r from-red-500 via-orange-400 to-green-500"
              style={{ width: "0%" }}
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            No data available
          </p>
        </Card>

        {/* Weekly Sleep Duration Chart */}
        <Card className="rounded-xl bg-card p-4 shadow-sm">
          <h3 className="mb-4 text-sm font-semibold text-foreground">
            Weekly Sleep Duration
          </h3>

          {/* Chart */}
          <div className="relative h-48">
            {/* Y-axis labels */}
            <div className="absolute left-0 top-0 flex h-full flex-col justify-between text-[10px] text-gray-500 dark:text-gray-400">
              <span>10</span>
              <span>8</span>
              <span>6</span>
              <span>4</span>
              <span>2</span>
            </div>

            {/* Goal line */}
            <div
              className="absolute left-6 right-0 border-t-2 border-dashed border-purple-400 dark:border-purple-600"
              style={{ top: `${((maxHours - goalHours) / maxHours) * 100}%` }}
            >
              <span className="absolute -top-2 right-0 text-[10px] text-purple-600 dark:text-purple-400">
                Goal
              </span>
            </div>

            {/* Chart area */}
            <div className="ml-6 flex h-full items-end justify-around gap-1">
              {weeklyData.map((item, index) => {
                const heightPercent = (item.hours / maxHours) * 100;
                return (
                  <div
                    key={index}
                    className="flex flex-1 flex-col items-center gap-1"
                  >
                    <div className="relative w-full" style={{ height: "160px" }}>
                      <div
                        className="absolute bottom-0 w-full rounded-t bg-purple-500"
                        style={{ height: `${heightPercent}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">
                      {item.day}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>

        {/* What This Means Section */}
        <Card className="rounded-xl bg-blue-50/80 p-4 shadow-sm dark:bg-blue-900/20">
          <div className="mb-2 flex items-center gap-2">
            <div className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-blue-600 dark:border-blue-400">
              <span className="text-xs font-bold text-blue-600 dark:text-blue-400">
                i
              </span>
            </div>
            <h3 className="text-sm font-semibold text-foreground">
              What This Means
            </h3>
          </div>

          <div className="space-y-3 text-xs text-gray-700 dark:text-gray-300">
            <p>
              <span className="font-semibold">Sleep recovery</span> measures
              total sleep duration and its accuracy for physiological
              restoration. Insufficient sleep directly increases stress load.
            </p>

            <p>
              <span className="font-semibold">At 6.2 hours,</span> you're
              accumulating sleep debt. This reduces your body's ability to
              regulate cortisol, impairs glucose metabolism, and slows muscle
              recovery.
            </p>

            <p>
              <span className="font-semibold">Cumulative sleep debt</span>{" "}
              compounds over days. Even one night of poor sleep can elevate your
              stress index for up to 48 hours.
            </p>
          </div>
        </Card>

        {/* How to Improve Section */}
        <Card className="rounded-xl bg-card p-4 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-foreground">
            How to Improve
          </h3>
          <ul className="space-y-2 text-xs text-gray-700 dark:text-gray-300">
            <li className="flex gap-2">
              <span className="text-purple-500">•</span>
              <span>Set a consistent bedtime and wake-up alarm</span>
            </li>
            <li className="flex gap-2">
              <span className="text-purple-500">•</span>
              <span>Avoid screens 1 hour before bed</span>
            </li>
            <li className="flex gap-2">
              <span className="text-purple-500">•</span>
              <span>Keep your bedroom cool (65-68°F / 18-20°C)</span>
            </li>
            <li className="flex gap-2">
              <span className="text-purple-500">•</span>
              <span>Limit caffeine after 2 PM</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default SleepRecoveryPage;
